// src/pages/PaymentManagement.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../config/supabase.config';
import PaymentEntry from '../components/payments/PaymentEntry';
import PaymentList from '../components/payments/PaymentList';
import PaymentSummary from '../components/payments/PaymentSummary';
import MonthlyReport from '../components/payments/MonthlyReport';
import PaymentReminders from '../components/payments/PaymentReminders';
import './UserDashboard.css';
import '../components/payments/PaymentManagement.css';

function PaymentManagement({ userData, userRole, onBack }) {
  const [activeTab, setActiveTab] = useState('entry');
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth() + 1);
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [payments, setPayments] = useState([]);
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(localStorage.getItem('darkMode') === 'true');
  const [maintenanceAmount, setMaintenanceAmount] = useState('2000');
  
  const [stats, setStats] = useState({
    totalResidents: 0,
    totalPaid: 0,
    totalPending: 0,
    totalAmount: 0,
    collectedAmount: 0
  });

  const isAdmin = userRole === 'admin' || userRole === 'superadmin';

  // Load configuration on component mount
  useEffect(() => {
    loadMaintenanceConfig();
    if (darkMode) {
      document.body.classList.add('dark-mode');
    }
  }, []);

  // Fetch data when month/year changes
  useEffect(() => {
    fetchInitialData();
  }, [currentMonth, currentYear, maintenanceAmount]);

  const loadMaintenanceConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('system_config')
        .select('config_value')
        .eq('config_key', 'maintenance_amount')
        .maybeSingle();
      
      if (data && data.config_value) {
        const dbAmount = data.config_value.toString();
        setMaintenanceAmount(dbAmount);
        localStorage.setItem('maintenanceAmount', dbAmount);
      } else {
        // Check localStorage as fallback
        const localAmount = localStorage.getItem('maintenanceAmount');
        if (localAmount) {
          setMaintenanceAmount(localAmount);
        }
      }
    } catch (error) {
      console.log('Error loading config:', error);
      const localAmount = localStorage.getItem('maintenanceAmount');
      if (localAmount) {
        setMaintenanceAmount(localAmount);
      }
    }
  };

  const saveMaintenanceConfig = async () => {
    try {
      const newAmount = parseFloat(maintenanceAmount);
      
      if (isNaN(newAmount) || newAmount < 0) {
        alert('Please enter a valid amount');
        return;
      }

      // Use upsert to handle both insert and update
      const { data, error } = await supabase
        .from('system_config')
        .upsert(
          { 
            config_key: 'maintenance_amount',
            config_value: newAmount,
            updated_at: new Date().toISOString()
          },
          { 
            onConflict: 'config_key'
          }
        )
        .select();

      if (error) {
        throw error;
      }
      
      // Update local storage
      localStorage.setItem('maintenanceAmount', newAmount.toString());
      
      // Recalculate stats with new amount
      calculateStats(residents, payments);
      
      alert('Maintenance amount updated successfully!');
      
    } catch (error) {
      console.error('Error saving config:', error);
      alert('Failed to save configuration: ' + error.message);
    }
  };

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const { data: residentData, error: residentError } = await supabase
        .from('residents')
        .select('*')
        .eq('account_status', 'active')
        .neq('role', 'superadmin')
        .order('flat_number');

      if (residentError) throw residentError;
      
      setResidents(residentData || []);

      const { data: paymentData, error: paymentError } = await supabase
        .from('payments')
        .select('*')
        .eq('payment_month', currentMonth)
        .eq('payment_year', currentYear)
        .order('payment_date', { ascending: false });

      if (paymentError) throw paymentError;
      
      const paymentsWithResidents = (paymentData || []).map(payment => {
        const resident = residentData?.find(r => r.id === payment.resident_id);
        return {
          ...payment,
          residents: resident || null
        };
      });
      
      setPayments(paymentsWithResidents);
      calculateStats(residentData || [], paymentData || []);

    } catch (error) {
      console.error('Error fetching data:', error);
      alert('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (residentsList, paymentsList) => {
    const residentsToUse = residentsList || residents;
    const paymentsToUse = paymentsList || payments;
    
    const uniqueFlats = [...new Set(residentsToUse.map(r => r.flat_number))];
    const totalFlats = uniqueFlats.length;
    
    const paidFlats = [...new Set(
      paymentsToUse
        .filter(p => p.status === 'paid')
        .map(p => {
          const resident = residentsToUse.find(r => r.id === p.resident_id);
          return resident ? resident.flat_number : null;
        })
        .filter(Boolean)
    )];
    
    const totalPaid = paidFlats.length;
    const totalPending = totalFlats - totalPaid;
    const totalAmount = totalFlats * parseFloat(maintenanceAmount);
    const collectedAmount = paymentsToUse
      .filter(p => p.status === 'paid')
      .reduce((sum, p) => sum + (p.amount || 0), 0);

    setStats({
      totalResidents: totalFlats,
      totalPaid,
      totalPending,
      totalAmount,
      collectedAmount
    });
  };

  const handlePaymentSaved = () => {
    fetchInitialData();
  };

  const handleMonthChange = (month, year) => {
    setCurrentMonth(month);
    setCurrentYear(year);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode');
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  if (!isAdmin) {
    return (
      <div className="dashboard-container">
        <div className="dashboard-content" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
          <div className="dashboard-card" style={{ textAlign: 'center', padding: '40px' }}>
            <h3>Access Restricted</h3>
            <p>Only admin users can access payment management.</p>
            <button onClick={onBack} className="btn-secondary">Go Back</button>
          </div>
        </div>
      </div>
    );
  }

  const tabConfig = [
    { key: 'entry', label: 'Record Payment', icon: '📝' },
    { key: 'list', label: 'Payment List', icon: '📋' },
    { key: 'report', label: 'Monthly Report', icon: '📊' },
    { key: 'reminders', label: 'Send Reminders', icon: '📱' }
  ];

  return (
    <div className={`dashboard-container payment-management-container ${darkMode ? 'dark-mode' : ''}`}>
      <div 
        className={`mobile-overlay ${mobileMenuOpen ? 'active' : ''}`}
        onClick={closeMobileMenu}
      ></div>

      {/* Sidebar Navigation */}
      <aside className={`dashboard-sidebar ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-header">
          <div className="user-info">
            <div className="user-avatar">
              {userData?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="user-details">
              <h3>{userData?.name}</h3>
              <p>Flat {userData?.flat_number}</p>
            </div>
          </div>
        </div>
        
        <nav className="sidebar-menu">
          <button
            className="menu-item"
            onClick={() => {
              closeMobileMenu();
              if (typeof onBack === 'function') {
                onBack();
              } else {
                console.error('onBack prop not provided');
                const isAdmin = userData?.role === 'admin' || userData?.role === 'superadmin';
                window.location.href = isAdmin ? '/admin-dashboard' : '/user-dashboard';
              }
            }}
          >
            <span className="menu-icon">🏠</span>
            <span className="menu-label">Dashboard</span>
          </button>
          
          <div className="sidebar-divider"></div>
          
          {tabConfig.map(tab => (
            <button
              key={tab.key}
              className={`menu-item ${activeTab === tab.key ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(tab.key);
                closeMobileMenu();
              }}
            >
              <span className="menu-icon">{tab.icon}</span>
              <span className="menu-label">{tab.label}</span>
            </button>
          ))}
          
          <div className="sidebar-divider"></div>
          
          <div className="month-selector">
            <label className="filter-label">Select Month</label>
            <select
              value={currentMonth}
              onChange={(e) => setCurrentMonth(parseInt(e.target.value))}
              className="month-select"
            >
              {[...Array(12)].map((_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Date(2024, i).toLocaleDateString('en-US', { month: 'long' })}
                </option>
              ))}
            </select>
            
            <select
              value={currentYear}
              onChange={(e) => setCurrentYear(parseInt(e.target.value))}
              className="year-select"
            >
              {[2024, 2025, 2026].map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </nav>
        
        <div className="sidebar-footer">
          <button className="theme-toggle" onClick={toggleDarkMode}>
            <span className="menu-icon">{darkMode ? '☀️' : '🌙'}</span>
            <span className="menu-label">{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </button>
        </div>
      </aside>
      
      {/* Main Content */}
      <main className="dashboard-main">
        {/* Mobile Header */}
        <header className="dashboard-header enhanced">
          <div className="header-left">
            <button 
              className={`mobile-menu-btn ${mobileMenuOpen ? 'active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <h2 className="page-title-mobile">💰 Payments</h2>
          </div>
        </header>

        <div className="dashboard-content">
          {/* Desktop Header */}
          <div className="payment-header">
            <div>
              <h2 className="page-title">💰 Payment Management</h2>
              <p className="page-subtitle">
                Managing payments for {new Date(currentYear, currentMonth - 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>

          {/* Maintenance Amount Configuration - MOVED TO MAIN CONTENT */}
          {isAdmin && (
            <div className="dashboard-card" style={{ marginBottom: '20px' }}>
              <h4 style={{ marginBottom: '15px', color: '#333' }}>Maintenance Amount Configuration</h4>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: '15px', flexWrap: 'wrap' }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    fontSize: '12px', 
                    color: '#666', 
                    marginBottom: '5px',
                    fontWeight: '600'
                  }}>
                    Monthly Amount (₹)
                  </label>
                  <input
                    type="number"
                    value={maintenanceAmount}
                    onChange={(e) => setMaintenanceAmount(e.target.value)}
                    style={{
                      padding: '10px 12px',
                      border: '2px solid #e0e0e0',
                      borderRadius: '6px',
                      fontSize: '16px',
                      width: '180px',
                      fontWeight: '600',
                      color: '#333'
                    }}
                    min="0"
                    step="100"
                  />
                </div>
                <button
                  onClick={saveMaintenanceConfig}
                  style={{
                    padding: '11px 24px',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.3)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  Save Amount
                </button>
              </div>
              <small style={{ 
                display: 'block', 
                marginTop: '10px', 
                color: '#999',
                fontSize: '13px'
              }}>
                This amount will be used for all new payment entries and calculations
              </small>
            </div>
          )}

          {/* Stats Summary */}
          <PaymentSummary stats={stats} loading={loading} />

          {/* Tab Navigation for Desktop */}
          <div className="payment-tabs desktop-only">
            {tabConfig.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div className="dashboard-card payment-content">
            {activeTab === 'entry' && (
              <PaymentEntry
                residents={residents}
                currentMonth={currentMonth}
                currentYear={currentYear}
                adminId={userData.id}
                maintenanceAmount={parseFloat(maintenanceAmount)}
                onPaymentSaved={handlePaymentSaved}
              />
            )}

            {activeTab === 'list' && (
              <PaymentList
                payments={payments}
                residents={residents}
                currentMonth={currentMonth}
                currentYear={currentYear}
                onMonthChange={handleMonthChange}
                onRefresh={fetchInitialData}
              />
            )}

            {activeTab === 'report' && (
              <MonthlyReport
                payments={payments}
                residents={residents}
                stats={stats}
                currentMonth={currentMonth}
                currentYear={currentYear}
                maintenanceAmount={parseFloat(maintenanceAmount)}
                onMonthChange={handleMonthChange}
              />
            )}

            {activeTab === 'reminders' && (
              <PaymentReminders
                residents={residents}
                payments={payments}
                currentMonth={currentMonth}
                currentYear={currentYear}
                maintenanceAmount={parseFloat(maintenanceAmount)}
              />
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

export default PaymentManagement;